import React, { useEffect, useMemo, useState } from 'react';

export interface LancamentoPendente {
    id: string;
    descricao: string;
    valor: number;
    dataRealizado: string;
    categoriaNome: string; // "Sem categoria" quando não houver
}

export interface PagamentoPendente {
    id: string;
    descricao: string;
    valor: number;
    dataPrevista: string;   // ISO (yyyy-MM-dd...) — usado para marcação padrão
    dataPrevistaLabel: string; // dd/MM/yyyy para exibição
    emAtraso: boolean;      // dataPrevista < hoje
}

interface Props {
    show: boolean;
    lancamentos: LancamentoPendente[];
    pagamentos?: PagamentoPendente[];
    carregando?: boolean;
    onClose: () => void;
    onConfirmar: (idsLancamentos: string[], idsPagamentos: string[]) => void;
}

const formatarValor = (valor: number) =>
    valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Futuros vêm marcados para remoção; em atraso ficam desmarcados (preservados como dívida).
const selecaoPadraoPagamentos = (pagamentos: PagamentoPendente[]) =>
    new Set(pagamentos.filter(p => !p.emAtraso).map(p => p.id));

/**
 * Modal exibido ao encerrar uma locação: lista os lançamentos pendentes da locação
 * agrupados por categoria. Por padrão todos vêm marcados e os grupos colapsados.
 * O usuário pode desmarcar grupos/itens, expandir ("destrinchar") cada categoria e
 * confirmar a remoção apenas dos selecionados.
 * Também exibe pagamentos não pagos: futuros vêm marcados, em atraso desmarcados.
 * O contador, o "Marcar todos" e a confirmação consideram lançamentos e pagamentos juntos.
 */
export const ModalLancamentosPendentes: React.FC<Props> = ({ show, lancamentos, pagamentos, carregando, onClose, onConfirmar }) => {
    const pagamentosLista = useMemo(() => pagamentos ?? [], [pagamentos]);
    const [selecionados, setSelecionados] = useState<Set<string>>(() => new Set(lancamentos.map(l => l.id)));
    const [selecionadosPag, setSelecionadosPag] = useState<Set<string>>(() => selecaoPadraoPagamentos(pagamentosLista));
    const [expandido, setExpandido] = useState<Record<string, boolean>>({});

    // Seleção padrão a cada abertura e sempre que as listas mudam.
    useEffect(() => {
        if (!show) return;
        setSelecionados(new Set(lancamentos.map(l => l.id)));
        setSelecionadosPag(selecaoPadraoPagamentos(pagamentosLista));
        setExpandido({});
    }, [show, lancamentos, pagamentosLista]);

    const grupos = useMemo(() => {
        const mapa = new Map<string, LancamentoPendente[]>();
        for (const l of lancamentos) {
            const chave = l.categoriaNome || 'Sem categoria';
            if (!mapa.has(chave)) mapa.set(chave, []);
            mapa.get(chave)!.push(l);
        }
        return Array.from(mapa.entries());
    }, [lancamentos]);

    // Contagem e envio derivados das listas exibidas, para contador, confirmação e remoção usarem os mesmos itens.
    const idsLancamentosMarcados = lancamentos.filter(l => selecionados.has(l.id)).map(l => l.id);
    const idsPagamentosMarcados = pagamentosLista.filter(p => selecionadosPag.has(p.id)).map(p => p.id);
    const totalItens = lancamentos.length + pagamentosLista.length;
    const totalMarcados = idsLancamentosMarcados.length + idsPagamentosMarcados.length;
    const todosMarcados = totalItens > 0 && totalMarcados === totalItens;

    const alternarTodos = () => {
        if (todosMarcados) {
            setSelecionados(new Set<string>());
            setSelecionadosPag(new Set<string>());
        } else {
            setSelecionados(new Set(lancamentos.map(l => l.id)));
            setSelecionadosPag(new Set(pagamentosLista.map(p => p.id)));
        }
    };

    const alternarItemPag = (id: string) => {
        const novo = new Set(selecionadosPag);
        if (novo.has(id)) novo.delete(id); else novo.add(id);
        setSelecionadosPag(novo);
    };

    const alternarGrupo = (itens: LancamentoPendente[]) => {
        const idsGrupo = itens.map(i => i.id);
        const todosDoGrupo = idsGrupo.every(id => selecionados.has(id));
        const novo = new Set(selecionados);
        idsGrupo.forEach(id => (todosDoGrupo ? novo.delete(id) : novo.add(id)));
        setSelecionados(novo);
    };

    const alternarItem = (id: string) => {
        const novo = new Set(selecionados);
        if (novo.has(id)) novo.delete(id); else novo.add(id);
        setSelecionados(novo);
    };

    const alternarExpandido = (categoria: string) => {
        setExpandido(prev => ({ ...prev, [categoria]: !prev[categoria] }));
    };

    if (!show) return null;

    return (
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 9998,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
        >
            <div style={{ background: '#fff', borderRadius: 8, padding: 20, width: 560, maxWidth: '92vw', maxHeight: '82vh', overflow: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,.25)' }}>
                <h3 style={{ marginTop: 0, color: '#1F467E' }}>Lançamentos pendentes da locação</h3>
                <p style={{ color: '#555', marginTop: 4 }}>
                    <strong>A locação já foi encerrada.</strong> Estes itens pendentes continuam atrelados a ela.
                    Marque os que deseja remover; o que ficar desmarcado permanece registrado. Os lançamentos
                    vêm marcados por padrão e você pode desmarcar ou expandir cada categoria.
                </p>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0' }}>
                    <button type="button" onClick={alternarTodos} disabled={totalItens === 0} style={{ background: 'none', border: 'none', color: '#1F467E', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                        {todosMarcados ? 'Desmarcar todos' : 'Marcar todos (remover todos)'}
                    </button>
                    <span style={{ color: '#888', fontSize: 13 }}>{totalMarcados} de {totalItens} selecionado(s)</span>
                </div>

                {pagamentosLista.length > 0 && (
                    <div style={{ margin: '8px 0 14px' }}>
                        <h4 style={{ color: '#1F467E', margin: '6px 0' }}>Pagamentos não pagos</h4>
                        <p style={{ color: '#555', marginTop: 0, fontSize: 13 }}>
                            Parcelas futuras vêm marcadas para remoção. As em atraso vêm desmarcadas
                            (preservadas como dívida). Marque-as só se quiser excluí-las também.
                        </p>
                        {pagamentosLista.map(p => (
                            <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', color: p.emAtraso ? '#B23' : '#444' }}>
                                <input type="checkbox" checked={selecionadosPag.has(p.id)} onChange={() => alternarItemPag(p.id)} />
                                <span style={{ flex: 1 }}>
                                    {p.descricao || 'Pagamento'} {p.emAtraso && <strong>(em atraso)</strong>}
                                </span>
                                <span style={{ color: '#777', fontSize: 13 }}>{p.dataPrevistaLabel}</span>
                                <span style={{ minWidth: 90, textAlign: 'right' }}>{formatarValor(p.valor)}</span>
                            </label>
                        ))}
                    </div>
                )}

                {grupos.map(([categoria, itens]) => {
                    const todosDoGrupoMarcados = itens.every(i => selecionados.has(i.id));
                    const algunsMarcados = !todosDoGrupoMarcados && itens.some(i => selecionados.has(i.id));
                    const totalGrupo = itens.reduce((acc, i) => acc + i.valor, 0);
                    return (
                        <div key={categoria} style={{ border: '1px solid #e6e6e6', borderRadius: 6, margin: '8px 0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px' }}>
                                <input
                                    type="checkbox"
                                    checked={todosDoGrupoMarcados}
                                    ref={el => { if (el) el.indeterminate = algunsMarcados; }}
                                    onChange={() => alternarGrupo(itens)}
                                />
                                <span
                                    onClick={() => alternarExpandido(categoria)}
                                    style={{ flex: 1, cursor: 'pointer', fontWeight: 600, color: '#333' }}
                                >
                                    {categoria} ({itens.length}) {formatarValor(totalGrupo)} {expandido[categoria] ? '▲' : '▼'}
                                </span>
                            </div>
                            {expandido[categoria] && (
                                <div style={{ padding: '0 10px 8px 30px' }}>
                                    {itens.map(i => (
                                        <label key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', color: '#444' }}>
                                            <input type="checkbox" checked={selecionados.has(i.id)} onChange={() => alternarItem(i.id)} />
                                            <span style={{ flex: 1 }}>{i.descricao.replace(/^\s*(\[\d+\/\d+\])\s*-\s*$/, '$1')}</span>
                                            <span style={{ color: '#777', fontSize: 13 }}>{i.dataRealizado}</span>
                                            <span style={{ minWidth: 90, textAlign: 'right' }}>{formatarValor(i.valor)}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                    <button type="button" onClick={onClose} disabled={carregando} style={{ padding: '8px 16px', border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
                        Fechar
                    </button>
                    {totalMarcados === 0 ? (
                        // Nada marcado: concluir mantendo tudo. O botão cinza sem resposta dava a impressão de que
                        // os pendentes impediam o encerramento (DL-194).
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={carregando}
                            style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#1F467E', color: '#fff', cursor: 'pointer' }}
                        >
                            Manter todos e concluir
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onConfirmar(idsLancamentosMarcados, idsPagamentosMarcados)}
                            disabled={carregando}
                            style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#E43F52', color: '#fff', cursor: 'pointer' }}
                        >
                            Remover selecionados
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ModalLancamentosPendentes;
